import React, { useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import saveFill from "@iconify/icons-eva/save-outline";
import {
  Dialog,
  TextField,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Switch,
  Autocomplete,
} from "@material-ui/core";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import { LoadingButton } from "@material-ui/lab";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { useSnackbar } from "src/components/snackbar";

import { Alert, AlertTitle } from "@material-ui/lab";
import LockIcon from "@material-ui/icons/Lock";
import { useSelector } from "react-redux";

import { clientesApi } from "src/services/clientesApi";

export default function FormDialog(props) {
  const { open, setOpen, refCliente } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [checked, setChecked] = useState(false);
  const [esClaveGestor, setEsClaveGestor] = useState(false);
  const [visibleParaCliente, setVisibleParaCliente] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  // Obtener sucursal activa para auditoría
  const {
    sucursal: { refSucursal },
  } = useSelector((state) => state.auth.user);

  const [agregarClavePortales] = clientesApi.useAgregarClavePortalesMutation();
  const [agregarClaveSitax] = clientesApi.useAgregarClaveSitaxMutation();

  const [tipoClave, setTipoClave] = useState({
    label: "Socio",
    value: "SOCIO",
  });

  /**
   * Se encarga de cerrar el modal de agregar empleado
   */
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  /**
   * Se encarga de hacer las verificaciones para los distinso campos del
   * formulario de agregar empleados.
   */
  const LoginSchema = Yup.object().shape({
    nombreUsuario: Yup.string().required(
      "Se debe ingresar el nombre de usuario",
    ),
    clave: Yup.string().required("Se debe ingresar una clave"),
    tipoClave: Yup.string().required("Debe seleccionar un tipo de cliente"),
    institucion: Yup.string()
      .required("Se debe ingresar una institución")
      .matches(/^[a-zA-Z0-9\s]+$/, {
        message:
          'La institución no puede contener caracteres especiales como , . " /',
        excludeEmptyString: true,
      }),
  });

  /**
   * Objeto encargado de validar los valores ingresados al formulario, según las
   * validaciones entregadas por LoginSchema.
   */
  const formik = useFormik({
    initialValues: {
      nombreUsuario: "",
      clave: "",
      tipoClave: "SOCIO",
      institucion: "",
      remember: true,
    },
    validationSchema: LoginSchema,
    onSubmit: () => {
      guardarDatos();
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, values } =
    formik;

  /**
   * Se encargar de guardar los datos ingresados por el usuario, ya validados
   * a la variable empleado, que es la que se guardará en la bd.
   */
  const guardarDatos = useCallback(async () => {
    const clavesCliente = {
      refCliente: refCliente,
      institucion: getFieldProps("institucion").value.toUpperCase(),
      clave: getFieldProps("clave").value,
      tipoClave: getFieldProps("tipoClave").value,
      nombreUsuario: getFieldProps("nombreUsuario").value,
    };

    const body = {
      ...clavesCliente,
      refSucursal,
      visibleCliente: visibleParaCliente,
      ...(esClaveGestor && { SITAX: "" }),
    };

    try {
      if (esClaveGestor) {
        await agregarClaveSitax(body).unwrap();
      } else {
        await agregarClavePortales(body).unwrap();
      }

      enqueueSnackbar("Clave agregada correctamente", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
      handleClose();
    } catch (error) {
      console.error("Error al agregar la clave: ", error);
      enqueueSnackbar(
        error?.data?.message ||
          error.message ||
          "Ocurrió un error desconocido al agregar la clave",
        {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        },
      );
    }
  }, [
    agregarClavePortales,
    agregarClaveSitax,
    enqueueSnackbar,
    esClaveGestor,
    getFieldProps,
    handleClose,
    refCliente,
    refSucursal,
    visibleParaCliente,
  ]);

  const handleShowPassword = () => {
    setShowPassword((showPassword) => !showPassword);
  };

  const handleChange = (event) => {
    if (checked) {
      setEsClaveGestor(false);
      values.institucion = "";
      values.nombreUsuario = "";
    } else {
      setEsClaveGestor(true);
      values.nombreUsuario = refCliente;
      values.institucion = "SITAX";
    }
    setChecked(event.target.checked);
  };
  return (
    <>
      <div>
        <Dialog open={open} aria-labelledby="form-dialog-title">
          <DialogTitle id="form-dialog-title">Claves Cliente</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Complete la información del cliente
            </DialogContentText>
            <Alert
              severity="info"
              icon={<LockIcon />}
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              <AlertTitle>Encriptación Automática</AlertTitle>
              La contraseña será encriptada automáticamente con{" "}
              <strong>AES-256-GCM</strong> antes de guardarla en la base de
              datos. Esto garantiza la seguridad de las credenciales.
            </Alert>

            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <TextField
                  disabled={esClaveGestor}
                  required
                  autoFocus
                  margin="dense"
                  id="nombreUsuario"
                  label="Nombre de Usuario"
                  fullWidth
                  {...getFieldProps("nombreUsuario")}
                  error={
                    esClaveGestor
                      ? false
                      : Boolean(touched.nombreUsuario && errors.nombreUsuario)
                  }
                  helperText={
                    esClaveGestor
                      ? false
                      : touched.nombreUsuario && errors.nombreUsuario
                  }
                />

                <TextField
                  required
                  fullWidth
                  autoComplete="new-password"
                  type={showPassword ? "text" : "password"}
                  label="Clave del cliente"
                  {...getFieldProps("clave")}
                  style={{ marginBottom: 15 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end">
                          <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={Boolean(touched.clave && errors.clave)}
                  helperText={touched.clave && errors.clave}
                />

                <Autocomplete
                  id="combo-b"
                  options={tipoClaveOptions}
                  value={tipoClave}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  getOptionLabel={(option) => option.label}
                  onChange={(event, newValue) => {
                    if (newValue !== null) {
                      values.tipoClave = newValue.value;
                      setTipoClave(newValue);
                    }
                  }}
                  style={{ marginBottom: 15 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      {...params}
                      label="Tipo de Cliente"
                      {...getFieldProps("tipoClave")}
                      error={Boolean(touched.tipoClave && errors.tipoClave)}
                      helperText={touched.tipoClave && errors.tipoClave}
                    />
                  )}
                />

                {!esClaveGestor && (
                  <FormControlLabel
                    value="end"
                    control={
                      <Switch
                        checked={visibleParaCliente}
                        onChange={(e) =>
                          setVisibleParaCliente(e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="Visible para el cliente en el portal"
                    labelPlacement="end"
                  />
                )}

                <FormControlLabel
                  value="end"
                  control={
                    <Switch
                      checked={checked}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label="Gestor"
                  labelPlacement="end"
                />
                <TextField
                  disabled={esClaveGestor}
                  required
                  margin="dense"
                  id="institucion"
                  label="Institucion"
                  fullWidth
                  {...getFieldProps("institucion")}
                  error={Boolean(touched.institucion && errors.institucion)}
                  helperText={touched.institucion && errors.institucion}
                />

                <DialogActions>
                  <LoadingButton onClick={handleClose} color="error">
                    Cancelar
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    color="primary"
                    startIcon={<Icon icon={saveFill} />}
                    loading={isSubmitting}
                  >
                    Guardar Claves Cliente
                  </LoadingButton>
                </DialogActions>
              </Form>
            </FormikProvider>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

const tipoClaveOptions = [
  { label: "Socio", value: "SOCIO" },
  { label: "Representante Legal", value: "REPRESENTANTE LEGAL" },
  { label: "Empresa", value: "EMPRESA" },
  { label: "Persona natural", value: "PERSONA NATURAL" },
];
